import type { ReleaseInput } from '../../types';

export interface QAOption {
  value: string;
  label: string;
}

export interface ReleaseFormProps {
  onSubmit: (input: ReleaseInput) => void;
  isLoading: boolean;
  prefillData?: ReleaseInput | null;
  isDemo?: boolean;
  onDemoSubmit?: () => void;
  onUnsavedChangesChange?: (hasChanges: boolean) => void;
  // 폼 화면에서 "내 정보" 모달을 열어야 할 때 사용 (예: GitHub 연결 유도)
  onRequestOpenProfile?: () => void;
}

export type InputMode = 'manual' | 'file';

export interface TestCaseAnalysis {
  total: number;
  passed: number;
  failed: number;
  coreStatus: string;
  coreStatusType?: 'verified' | 'partial' | 'failed' | 'unverified';
  edgeStatus: string;
  edgeStatusType?: 'verified' | 'partial' | 'failed' | 'unverified';
  coreTotal?: number;
  corePassed?: number;
  coreFailed?: number;
  edgeTotal?: number;
  edgePassed?: number;
  edgeFailed?: number;
  parseWarning?: string;
}

export interface ScreenshotAnalysis {
  uiComponents: string[];
  userFlow: string[];
  kpiSuggestions: string[];
  suggestedFeatureName?: string;
}

// 허용된 파일 형식
export const PLANNING_ALLOWED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/markdown',
];

export const PLANNING_ALLOWED_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.pdf',
  '.txt',
  '.md',
];

export const TESTCASE_ALLOWED_EXTENSIONS = ['.csv', '.json', '.md'];

export const isPlanningFileValid = (file: File): boolean => {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return (
    PLANNING_ALLOWED_TYPES.includes(file.type) ||
    PLANNING_ALLOWED_EXTENSIONS.includes(ext)
  );
};

export const isTestCaseFileValid = (file: File): boolean => {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return TESTCASE_ALLOWED_EXTENSIONS.includes(ext);
};

/**
 * 파일명 생성 유틸 함수
 * (제목)_기획의도_#(number).(확장자) 또는 (제목)_테스트케이스_#(number).(확장자)
 */
export const generateDisplayFileName = (
  featureName: string,
  fileType: 'planning' | 'testcase',
  index: number,
  originalFileName: string
): string => {
  // 확장자 추출
  const ext = originalFileName.includes('.') 
    ? '.' + originalFileName.split('.').pop()?.toLowerCase() 
    : '';
  
  // featureName이 비어있으면 원본 파일명 반환
  if (!featureName.trim()) {
    return originalFileName;
  }
  
  // featureName에서 특수문자 제거 (한글, 영문, 숫자, 공백, -, _ 허용)
  const safeFeatureName = featureName
    .replace(/[^\w\s가-힣ㄱ-ㅎㅏ-ㅣ\-_]/g, '_')
    .trim()
    .replace(/\s+/g, '_');
  
  const typeLabel = fileType === 'planning' ? '기획의도' : '테스트케이스';
  
  return `${safeFeatureName}_${typeLabel}_#${index + 1}${ext}`;
};

